"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";

import { NavItem } from "types";
import { docsConfig } from "@/config/docs";
import { cn } from "@/lib/utils";

export interface DocsSidebarNavProps {
  setOpen?: (boolean) => void;
}

export function DocsSidebarNav({ setOpen }: DocsSidebarNavProps) {
  const pathname = usePathname();
  const items = docsConfig.sidebarNav;

  return items.length > 0 ? (
    <div className="w-full">
      {items.map((item) => (
        <div key={item.title} className={cn("pb-8")}>
          <h4 className="mb-1 rounded-md py-1 text-base font-medium md:px-2 md:text-sm">
            {item.title}
          </h4>
          {item.items ? (
            <DocsSidebarNavItems
              setOpen={setOpen}
              items={item.items}
              pathname={pathname}
            />
          ) : null}
        </div>
      ))}
    </div>
  ) : null;
}

interface DocsSidebarNavItemsProps {
  items: NavItem[];
  pathname: string | null;
  setOpen?: (boolean) => void;
  depth?: number;
}

export function DocsSidebarNavItems({
  items,
  setOpen,
  pathname,
  depth = 0,
}: DocsSidebarNavItemsProps) {
  return items?.length > 0 ? (
    <div className="grid grid-flow-row auto-rows-max text-[15px] md:text-sm">
      {items.map((item) =>
        !item.disabled && item.href ? (
          <div key={item.title + item.href}>
            <Link
              href={item.href}
              onClick={() => {
                if (setOpen) setOpen(false);
              }}
              className={cn(
                "flex w-full items-center rounded-md py-1.5 text-muted-foreground hover:underline",
                {
                  "font-medium text-blue-600 dark:text-blue-400":
                    pathname === item.href ||
                    (item.items?.some((sub) => pathname === sub.href) ?? false),
                },
                depth === 0 ? "px-2" : "pl-6 pr-2 text-[13px] md:text-xs",
              )}
              target={item.external ? "_blank" : ""}
              rel={item.external ? "noreferrer" : ""}
            >
              {item.title}
            </Link>
            {item.items && item.items.length > 0 && (
              <DocsSidebarNavItems
                items={item.items}
                pathname={pathname}
                setOpen={setOpen}
                depth={depth + 1}
              />
            )}
          </div>
        ) : (
          <span
            key={item.title + item.href}
            className={cn(
              "flex w-full cursor-not-allowed items-center rounded-md py-1.5 opacity-60",
              depth === 0 ? "px-2" : "pl-6 pr-2 text-[13px] md:text-xs",
            )}
          >
            {item.title}
          </span>
        ),
      )}
    </div>
  ) : null;
}
